import { useEffect, useState } from "react";
import { Receipt, Loader2, Lock, CheckCircle2, Send, EyeOff, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useEmployeeApi } from "../employeeApi";

const dkk = (v) => (v === null || v === undefined ? "—" : new Intl.NumberFormat("da-DK", { style: "currency", currency: "DKK", maximumFractionDigits: 2 }).format(v));
const STATUS_TONE = { draft: "bg-amber-100 text-amber-800", sent: "bg-blue-100 text-blue-800", paid: "bg-emerald-100 text-emerald-800" };

export default function InvoiceDraftCard({ jobId, refreshKey, onChanged }) {
    const api = useEmployeeApi();
    const [inv, setInv] = useState(null);
    const [busy, setBusy] = useState("");

    useEffect(() => { api.getInvoiceDraft(jobId).then(setInv).catch(() => setInv({ exists: false, error: true })); }, [api, jobId, refreshKey]);

    const approve = async () => {
        setBusy("approve");
        try { setInv(await api.approveInvoiceDraft(jobId)); toast.success("Fakturakladde godkendt"); onChanged?.(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke godkende"); }
        finally { setBusy(""); }
    };
    const reopen = async () => {
        setBusy("reopen");
        try { setInv(await api.reopenInvoiceDraft(jobId)); toast.success("Genåbnet til rettelse"); onChanged?.(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke genåbne"); }
        finally { setBusy(""); }
    };
    const send = async () => {
        setBusy("send");
        try { await api.sendInvoiceDraft(jobId); toast.success("Faktura sendt"); }
        catch (e) { toast.error(e?.response?.data?.detail || "Afsendelse afvist"); }
        finally { setBusy(""); }
    };

    if (!inv) return <section className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></section>;

    return (
        <section className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm" data-testid="invoice-draft-card">
            <div className="flex items-center justify-between mb-3">
                <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Receipt className="w-3.5 h-3.5" />Fakturakladde</h2>
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${STATUS_TONE[inv.status] || "bg-slate-100 text-slate-600"}`} data-testid="invoice-status">{inv.status_label}</span>
            </div>

            {!inv.exists ? (
                <p className="text-sm text-slate-500" data-testid="invoice-empty">Kladden oprettes automatisk med opgavens tid og materialer, når opgaven afsluttes.</p>
            ) : (
                <>
                    {inv.customer_name && <p className="text-sm font-semibold text-slate-900 mb-2">{inv.customer_name}{inv.number ? ` · Faktura ${inv.number}` : ""}</p>}
                    <ul className="divide-y divide-slate-100 rounded-xl border border-slate-100 overflow-hidden" data-testid="invoice-lines">
                        {inv.line_items.map((li, i) => (
                            <li key={i} className="flex items-center justify-between gap-3 px-3 py-2 text-sm bg-slate-50/50">
                                <div className="min-w-0"><p className="text-slate-800 truncate">{li.description}</p><p className="text-xs text-slate-500">{li.qty} {li.unit}{inv.can_view_economy && li.unit_price != null ? ` × ${dkk(li.unit_price)}` : ""}</p></div>
                                {inv.can_view_economy && <span className="font-semibold tabular-nums text-slate-800 shrink-0">{dkk(li.line_total)}</span>}
                            </li>
                        ))}
                    </ul>
                    {inv.can_view_economy ? (
                        <div className="mt-3 space-y-1 text-sm" data-testid="invoice-totals">
                            <div className="flex justify-between text-slate-500"><span>Subtotal</span><span className="tabular-nums">{dkk(inv.subtotal)}</span></div>
                            <div className="flex justify-between text-slate-500"><span>Moms 25 %</span><span className="tabular-nums">{dkk(inv.vat_total)}</span></div>
                            <div className="flex justify-between font-bold text-slate-900 text-base pt-1 border-t border-slate-100"><span>Total</span><span className="tabular-nums" data-testid="invoice-total">{dkk(inv.total)}</span></div>
                        </div>
                    ) : (
                        <p className="mt-3 text-xs text-slate-500 flex items-center gap-1.5 bg-slate-50 rounded-lg px-3 py-2" data-testid="invoice-economy-hidden"><EyeOff className="w-3.5 h-3.5" />Beløb er skjult — kræver rettigheden “Må se økonomi”.</p>
                    )}
                    {inv.data_quality_warning && <p className="mt-2 text-xs text-amber-700 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" />{inv.data_quality_warning}</p>}
                    {inv.approved_at && <p className="mt-2 text-xs text-emerald-700 flex items-center gap-1.5" data-testid="invoice-approved"><CheckCircle2 className="w-3.5 h-3.5" />Godkendt af {inv.approved_by}</p>}
                    {inv.locked && <p className="mt-2 text-xs text-slate-600 flex items-center gap-1.5" data-testid="invoice-locked-note"><Lock className="w-3.5 h-3.5" />Låst efter godkendelse — tid, materialer og priser kan ikke ændres.</p>}

                    <div className="mt-3 grid grid-cols-2 gap-2">
                        {inv.reopenable ? (
                            <button onClick={reopen} disabled={!inv.can_approve || busy === "reopen"} data-testid="invoice-reopen"
                                className="h-10 rounded-xl border border-amber-300 bg-amber-50 text-sm font-semibold text-amber-800 flex items-center justify-center gap-1.5 disabled:opacity-40">
                                {busy === "reopen" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-3.5 h-3.5" />} Genåbn til rettelse
                            </button>
                        ) : (
                            <button onClick={approve} disabled={!inv.can_approve || inv.status !== "draft" || !!inv.approved_at || busy === "approve"} data-testid="invoice-approve"
                                className="h-10 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 flex items-center justify-center gap-1.5 disabled:opacity-40">
                                {busy === "approve" ? <Loader2 className="w-4 h-4 animate-spin" /> : inv.can_approve ? <CheckCircle2 className="w-4 h-4" /> : <Lock className="w-3.5 h-3.5" />} Godkend
                            </button>
                        )}
                        <button onClick={send} disabled={!inv.can_send || !inv.send_available || inv.demo_send_blocked || busy === "send"} data-testid="invoice-send"
                            className="h-10 rounded-xl bg-slate-900 text-white text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-40">
                            {busy === "send" ? <Loader2 className="w-4 h-4 animate-spin" /> : inv.can_send ? <Send className="w-4 h-4" /> : <Lock className="w-3.5 h-3.5" />} Send
                        </button>
                    </div>
                    <p className="mt-2 text-[11px] text-slate-400" data-testid="invoice-rights-hint">
                        {inv.send_unavailable_reason || "Fakturaer sendes fra webdashboardet."}
                    </p>
                </>
            )}
        </section>
    );
}
